import { z } from 'zod';

/**
 * Identity of the real-time push that keeps the agent PR badge / hover card
 * live. The server emits it via
 * `ctx.services.broadcastService.broadcastPluginEvent(GITHUB_PLUGIN_NAME,
 * GITHUB_PR_UPDATED_EVENT, payload, orgId)` from the `pull_request` webhook
 * receiver, and the web consumes it with
 * `usePluginEvent(GITHUB_PLUGIN_NAME, GITHUB_PR_UPDATED_EVENT, ...)` inside
 * `usePullRequestDetails`. Kept as shared constants (alongside
 * {@link ../shared/install-events.js#GITHUB_PLUGIN_NAME}) so the two ends can
 * never drift.
 */
export const GITHUB_PR_UPDATED_EVENT = 'pr_updated';

/**
 * Payload broadcast to the owning org each time a `pull_request` webhook fires.
 * Carries exactly the live facts the PR chip renders, so the web can write them
 * straight into the `github-pr-details` query cache without a follow-up round
 * trip to GitHub — an open badge flips to merged / closed / draft the moment the
 * webhook lands, rather than waiting on the chip's 5-minute stale window.
 *
 * `owner` / `repo` / `number` are the cache-key coordinates; they mirror what
 * the chip parses out of the PR URL so the pushed row lands on the same slot.
 * `state` is narrowed to GitHub's only two PR states (a webhook `state` string
 * is always `open` or `closed`).
 */
export const GitHubPrUpdatedSchema = z.object({
  owner: z.string(),
  repo: z.string(),
  number: z.number().int().positive(),
  url: z.string(),
  title: z.string(),
  state: z.enum(['open', 'closed']),
  merged: z.boolean(),
  draft: z.boolean(),
  headBranch: z.string(),
  baseBranch: z.string(),
  /**
   * GitHub's `updated_at` for this row, epoch ms. Webhooks arrive out of
   * order, so the web drops a push older than the details it already holds.
   * Optional only so a push from a server that predates it still parses; the
   * web ignores such an unversioned push once it holds a versioned row, and
   * the chip catches up through the stored association state instead.
   */
  updatedAt: z.number().nullable().optional(),
});

export type GitHubPrUpdated = z.infer<typeof GitHubPrUpdatedSchema>;
